import { useEffect, useMemo, useState } from "react";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import {
  Loader2, Layers, Plus, Trash2, Save, MapPin, Calculator, Rocket,
  Zap, Sun, Moon, CloudRain, Trash, CheckCircle2, ArrowRight,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const DEMAND_COLORS = {
  high:   { fill: "#EF4444", stroke: "#B91C1C", label: "Forte", cls: "bg-red-500" },
  medium: { fill: "#F59E0B", stroke: "#B45309", label: "Moyenne", cls: "bg-amber-500" },
  low:    { fill: "#10B981", stroke: "#047857", label: "Faible", cls: "bg-emerald-500" },
};

const MULT_ICONS = { peak: Zap, night: Moon, rain: CloudRain };

function ZoneCircle({ zone }) {
  // Radius approximation: 1° ≈ 111 km → radius_km / 111 → not perfect but fine for display.
  const size = Math.max(24, Math.min(220, zone.radius_km * 40));
  const color = DEMAND_COLORS[zone.demand_level] || DEMAND_COLORS.medium;
  return (
    <AdvancedMarker position={{ lat: zone.lat, lng: zone.lng }}>
      <div
        style={{
          width: size,
          height: size,
          background: `${color.fill}55`,
          border: `2px solid ${color.stroke}`,
          borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, color: color.stroke, fontWeight: 800,
          fontFamily: "Sora",
        }}
      >
        {zone.name}
      </div>
    </AdvancedMarker>
  );
}

export default function AdminExpressZonesTarifs() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [cfg, setCfg] = useState(null);

  // Simulator
  const [simVt, setSimVt] = useState("");
  const [simDist, setSimDist] = useState(5);
  const [simDur, setSimDur] = useState(15);
  const [simOrigin, setSimOrigin] = useState("");
  const [simDest, setSimDest] = useState("");
  const [simActive, setSimActive] = useState([]);
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/express/config").then(({ data }) => {
      setCfg({
        ...data,
        zones: data.zones || [],
        zone_pricing: data.zone_pricing || {},
        multipliers: data.multipliers || {},
      });
      if (data.vehicle_types?.length) setSimVt(data.vehicle_types[0].id);
    }).catch(() => toast.error("Impossible de charger la configuration"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const zones = cfg?.zones || [];
  const zonePricing = cfg?.zone_pricing || {};
  const multipliers = cfg?.multipliers || {};

  const mapCenter = useMemo(() => {
    if (!zones.length) return { lat: 4.0511, lng: 9.7679 }; // Douala default
    const avg = zones.reduce((acc, z) => ({ lat: acc.lat + z.lat, lng: acc.lng + z.lng }), { lat: 0, lng: 0 });
    return { lat: avg.lat / zones.length, lng: avg.lng / zones.length };
  }, [zones]);

  const updateZone = (i, patch) => setCfg((c) => ({ ...c, zones: c.zones.map((z, idx) => (idx === i ? { ...z, ...patch } : z)) }));
  const removeZone = (i) => setCfg((c) => ({ ...c, zones: c.zones.filter((_, idx) => idx !== i) }));
  const addZone = () => setCfg((c) => ({
    ...c,
    zones: [...(c.zones || []), { id: `zone-${(c.zones || []).length + 1}`, name: "Nouvelle zone", lat: 4.0511, lng: 9.7679, radius_km: 2, demand_level: "medium" }],
  }));

  const updateMatrix = (key, field, value) => {
    setCfg((c) => ({
      ...c,
      zone_pricing: { ...c.zone_pricing, [key]: { ...(c.zone_pricing[key] || { base: 0, per_km: 0, per_min: 0, min_fare: 0 }), [field]: Number(value || 0) } },
    }));
  };

  const removeMatrixEntry = (key) => {
    setCfg((c) => {
      const zp = { ...c.zone_pricing };
      delete zp[key];
      return { ...c, zone_pricing: zp };
    });
  };

  const updateMultiplier = (key, patch) => setCfg((c) => ({ ...c, multipliers: { ...c.multipliers, [key]: { ...c.multipliers[key], ...patch } } }));

  const save = async (publish = false) => {
    if (publish) setPublishing(true); else setSaving(true);
    try {
      await api.put("/admin/express/config", {
        zones: cfg.zones,
        zone_pricing: cfg.zone_pricing,
        multipliers: cfg.multipliers,
        publish,
      });
      toast.success(publish ? "Configuration publiée ✓" : "Configuration enregistrée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setPublishing(false); setSaving(false);
    }
  };

  const runSimulate = async () => {
    if (!simVt) { toast.error("Choisir un type de véhicule"); return; }
    setSimulating(true);
    try {
      const { data } = await api.post("/admin/express/simulate", {
        vehicle_type: simVt,
        distance_km: simDist,
        duration_min: simDur,
        origin_zone_id: simOrigin,
        destination_zone_id: simDest,
        active_multipliers: simActive,
      });
      setSimResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur simulation");
    } finally { setSimulating(false); }
  };

  const toggleSimMultiplier = (k) => setSimActive((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]));

  if (loading || !cfg) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-express-zones-tarifs">
      {/* Zones map */}
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#F2A93B]" />
            <h3 className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Carte des zones</h3>
          </div>
          <Button size="sm" onClick={addZone} className="bg-[#0F2B46] text-white" data-testid="add-zone-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une zone
          </Button>
        </div>
        {MAPS_KEY ? (
          <div className="rounded-xl overflow-hidden h-[320px] mb-3 border border-[#E4E0D8]">
            <APIProvider apiKey={MAPS_KEY}>
              <Map
                mapId="admin-express-zones-map"
                defaultCenter={mapCenter}
                defaultZoom={12}
                gestureHandling="cooperative"
                disableDefaultUI
                style={{ width: "100%", height: "100%" }}
              >
                {zones.map((z) => <ZoneCircle key={z.id} zone={z} />)}
              </Map>
            </APIProvider>
          </div>
        ) : (
          <div className="h-[240px] rounded-xl bg-slate-100 flex items-center justify-center text-sm text-[#5B6B7A]">Clé Google Maps manquante</div>
        )}
        <div className="flex gap-4 text-[11px] text-[#5B6B7A]">
          {Object.entries(DEMAND_COLORS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${v.cls}`} /> Demande {v.label}
            </span>
          ))}
        </div>

        {/* Zone list */}
        <div className="mt-4 space-y-2">
          {zones.map((z, i) => (
            <div key={i} className="p-2.5 rounded-xl border border-[#E4E0D8] grid grid-cols-1 sm:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`zone-row-${z.id}`}>
              <div>
                <Input value={z.name} onChange={(e) => updateZone(i, { name: e.target.value })} placeholder="Nom" className="h-8 text-xs" data-testid={`zone-name-${z.id}`} />
                <Input value={z.id} onChange={(e) => updateZone(i, { id: e.target.value })} placeholder="id" className="h-7 text-[11px] font-mono mt-1" />
              </div>
              <Input type="number" step="0.0001" value={z.lat} onChange={(e) => updateZone(i, { lat: parseFloat(e.target.value || 0) })} placeholder="Latitude" className="h-8 text-xs" data-testid={`zone-lat-${z.id}`} />
              <Input type="number" step="0.0001" value={z.lng} onChange={(e) => updateZone(i, { lng: parseFloat(e.target.value || 0) })} placeholder="Longitude" className="h-8 text-xs" data-testid={`zone-lng-${z.id}`} />
              <Input type="number" step="0.5" value={z.radius_km} onChange={(e) => updateZone(i, { radius_km: parseFloat(e.target.value || 0) })} placeholder="Rayon km" className="h-8 text-xs" data-testid={`zone-radius-${z.id}`} />
              <Select value={z.demand_level} onValueChange={(v) => updateZone(i, { demand_level: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`zone-demand-${z.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Forte</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                </SelectContent>
              </Select>
              <Button size="icon" variant="ghost" onClick={() => removeZone(i)} className="text-[#EF4444]" data-testid={`zone-remove-${z.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {zones.length === 0 && <div className="text-center text-xs text-[#5B6B7A] py-6">Aucune zone. Cliquez sur « Ajouter une zone ».</div>}
        </div>
      </div>

      {/* Zone-to-Zone matrix */}
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight className="w-5 h-5 text-[#F2A93B]" />
          <h3 className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Tarifs zone à zone</h3>
          <span className="text-[11px] text-[#5B6B7A] ml-auto">Écrase la formule de base pour un couple précis (Origine → Destination).</span>
        </div>
        {zones.length < 2 ? (
          <div className="py-6 text-center text-xs text-[#5B6B7A]">Ajoutez au moins deux zones pour définir une matrice.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">
                  <th className="py-1.5 pr-2">Origine → Destination</th>
                  <th className="py-1.5 pr-2">Base (F)</th>
                  <th className="py-1.5 pr-2">Par km</th>
                  <th className="py-1.5 pr-2">Par min</th>
                  <th className="py-1.5 pr-2">Min. course</th>
                  <th className="py-1.5 pr-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {zones.flatMap((a) =>
                  zones.filter((b) => b.id !== a.id).map((b) => {
                    const key = `${a.id}|${b.id}`;
                    const v = zonePricing[key];
                    return (
                      <tr key={key} className={`border-b border-[#F0EDE6] ${v ? "bg-[#FEF7EC]" : ""}`} data-testid={`matrix-row-${key}`}>
                        <td className="py-1.5 pr-2 font-semibold text-[#142433]">
                          {a.name} <ArrowRight className="w-3 h-3 inline mx-1 text-[#5B6B7A]" /> {b.name}
                        </td>
                        <td className="py-1.5 pr-2"><Input type="number" value={v?.base ?? ""} placeholder="—" onChange={(e) => updateMatrix(key, "base", e.target.value)} className="h-7 w-20 text-xs" data-testid={`matrix-base-${key}`} /></td>
                        <td className="py-1.5 pr-2"><Input type="number" value={v?.per_km ?? ""} placeholder="—" onChange={(e) => updateMatrix(key, "per_km", e.target.value)} className="h-7 w-20 text-xs" data-testid={`matrix-perkm-${key}`} /></td>
                        <td className="py-1.5 pr-2"><Input type="number" value={v?.per_min ?? ""} placeholder="—" onChange={(e) => updateMatrix(key, "per_min", e.target.value)} className="h-7 w-20 text-xs" data-testid={`matrix-permin-${key}`} /></td>
                        <td className="py-1.5 pr-2"><Input type="number" value={v?.min_fare ?? ""} placeholder="—" onChange={(e) => updateMatrix(key, "min_fare", e.target.value)} className="h-7 w-20 text-xs" data-testid={`matrix-minfare-${key}`} /></td>
                        <td className="py-1.5 pr-2">
                          {v && <button onClick={() => removeMatrixEntry(key)} className="text-[#EF4444] hover:text-[#B91C1C]" data-testid={`matrix-remove-${key}`}><Trash className="w-3.5 h-3.5" /></button>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Multipliers */}
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sun className="w-5 h-5 text-[#F2A93B]" />
          <h3 className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Multiplicateurs dynamiques</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {["peak", "night", "rain"].map((k) => {
            const m = multipliers[k] || { enabled: false, factor: 1, label: k };
            const Icon = MULT_ICONS[k] || Zap;
            return (
              <div key={k} className={`p-3 rounded-xl border-2 ${m.enabled ? "border-[#F2A93B] bg-[#FEF7EC]" : "border-[#E4E0D8] bg-white"}`} data-testid={`multiplier-card-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 font-bold text-[#0F2B46] text-sm" style={{ fontFamily: "Sora" }}>
                    <Icon className="w-4 h-4" /> {k === "peak" ? "Heures de pointe" : k === "night" ? "Nuit" : "Pluie"}
                  </div>
                  <Switch checked={!!m.enabled} onCheckedChange={(v) => updateMultiplier(k, { enabled: v })} data-testid={`multiplier-toggle-${k}`} />
                </div>
                <p className="text-[11px] text-[#5B6B7A] mb-2 min-h-[28px]">{m.label || ""}</p>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Facteur</label>
                <Input type="number" step="0.05" value={m.factor} onChange={(e) => updateMultiplier(k, { factor: parseFloat(e.target.value || 1) })} className="h-8 text-sm" data-testid={`multiplier-factor-${k}`} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Simulator */}
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-5 h-5 text-[#F2A93B]" />
          <h3 className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Simulateur de prix</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Type de véhicule</label>
            <Select value={simVt} onValueChange={setSimVt}>
              <SelectTrigger className="h-9 text-sm" data-testid="sim-vehicle-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                {(cfg.vehicle_types || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.emoji} {t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Distance (km)</label>
            <Input type="number" step="0.1" value={simDist} onChange={(e) => setSimDist(parseFloat(e.target.value || 0))} className="h-9 text-sm" data-testid="sim-distance" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Durée (min)</label>
            <Input type="number" step="1" value={simDur} onChange={(e) => setSimDur(parseFloat(e.target.value || 0))} className="h-9 text-sm" data-testid="sim-duration" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Zone origine (optionnel)</label>
            <Select value={simOrigin || "none"} onValueChange={(v) => setSimOrigin(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="sim-origin"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Zone destination (optionnel)</label>
            <Select value={simDest || "none"} onValueChange={(v) => setSimDest(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="sim-destination"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={runSimulate} disabled={simulating} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold w-full" data-testid="sim-run">
              {simulating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calculator className="w-4 h-4 mr-1" />} Simuler
            </Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {["peak", "night", "rain"].map((k) => {
            const on = simActive.includes(k);
            const Icon = MULT_ICONS[k] || Zap;
            return (
              <button key={k} type="button" onClick={() => toggleSimMultiplier(k)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${on ? "bg-[#F2A93B] border-[#F2A93B] text-[#0F2B46]" : "bg-white border-[#CBD5E1] text-[#5B6B7A]"}`}
                data-testid={`sim-mult-toggle-${k}`}>
                <Icon className="w-3 h-3" /> {k === "peak" ? "Pointe" : k === "night" ? "Nuit" : "Pluie"}
              </button>
            );
          })}
        </div>
        {simResult && (
          <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-[#0F2B46] to-[#173A5E] text-white" data-testid="sim-result">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-[#CFE0EF]" style={{ fontFamily: "Sora" }}>Prix simulé</div>
              <div className="text-3xl font-extrabold text-[#F2A93B]" style={{ fontFamily: "Sora" }} data-testid="sim-result-total">{simResult.total_xaf.toLocaleString("fr-FR").replace(/,/g, " ")} F</div>
            </div>
            <div className="text-[11px] text-[#B9CBDA] mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>Base : <b className="text-white">{simResult.base_used}</b> F</div>
              <div>Par km : <b className="text-white">{simResult.per_km_used}</b> F</div>
              <div>Par min : <b className="text-white">{simResult.per_min_used}</b> F</div>
              <div>Facteur : <b className="text-white">×{simResult.combined_factor}</b>{simResult.surge_multiplier !== 1 ? ` (surge ×${simResult.surge_multiplier})` : ""}</div>
            </div>
            {simResult.override_applied && (
              <div className="mt-2 text-[11px] text-[#F2A93B] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Tarif zone à zone appliqué</div>
            )}
          </div>
        )}
      </div>

      {/* Save / Publish */}
      <div className="flex items-center justify-between gap-3 sticky bottom-2 z-10 bg-white border border-[#E4E0D8] rounded-2xl px-4 py-3 shadow-md">
        <div className="text-[11px] text-[#5B6B7A]">
          <span className="font-semibold text-[#0F2B46]">Version {cfg.version || 1}</span>
          {cfg.published_at && <span> · Publié le {cfg.published_at.split("T")[0]}</span>}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save(false)} disabled={saving || publishing} variant="outline" className="border-[#CBD5E1]" data-testid="zones-save-draft">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer (brouillon)
          </Button>
          <Button onClick={() => save(true)} disabled={saving || publishing} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="zones-publish">
            {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Rocket className="w-4 h-4 mr-1" />} Publier
          </Button>
        </div>
      </div>
    </div>
  );
}
